import type { MouseEventHandler } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { breakpointSizes, mediaQueryUp } from 'components/utils/breakpoint'
import {
  thereNoPlaceLikeHomePopupImages,
  thereNoPlaceLikeHomePopupMessages,
  thereNoPlaceLikeHomePopupVariantConfig,
  type ThereNoPlaceLikeHomePopupMarket,
  type ThereNoPlaceLikeHomePopupVariant,
} from './thereNoPlaceLikeHomePopupConfig'
import type { ThereNoPlaceLikeHomePopupActionClickHandler } from './useThereNoPlaceLikeHomePopupActions'

interface Props {
  market: ThereNoPlaceLikeHomePopupMarket
  variant: ThereNoPlaceLikeHomePopupVariant
  onActionClick: ThereNoPlaceLikeHomePopupActionClickHandler
  onClose: MouseEventHandler<HTMLElement>
  onRemindMeNextTimeClick: MouseEventHandler<HTMLButtonElement>
}

const ImageContainer = styled.div`
  height: ${rem(150)};
  overflow: hidden;
  position: relative;

  ${mediaQueryUp.tablet} {
    height: ${rem(200)};
  }

  picture {
    display: block;
    height: 100%;
    width: 100%;
  }

  img {
    display: block;
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
`

const CloseButtonContainer = styled.div`
  position: absolute;
  inset-inline-end: ${rem(12)};
  top: ${rem(12)};
`

const Content = styled.div`
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(18)} ${rem(24)} ${rem(24)};
  }
`

const PromptButton = styled(AssistChip)`
  height: auto;
  min-width: 0;
  padding: ${rem(6)} ${rem(12)};
`

const Actions = styled(Group)`
  padding-top: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(16)};
  }
`

const PrimaryAction = styled(TextButton)`
  order: 1;

  ${mediaQueryUp.tablet} {
    order: 2;
  }
`

function ThereNoPlaceLikeHomePopupContent(props: Props) {
  const { market, variant, onActionClick, onClose, onRemindMeNextTimeClick } = props
  const intl = useIntl()
  const variantConfig = thereNoPlaceLikeHomePopupVariantConfig[market][variant]
  const popupImage = thereNoPlaceLikeHomePopupImages[market]
  const modalSize = variant === 'variant' ? 'S' : 'L'

  return (
    <ModalBase size={modalSize} mode="default" dismissible onClose={onClose}>
      <ModalBody>
        <ImageContainer>
          <picture>
            <source
              media={`(min-width: ${breakpointSizes.tablet.min}px)`}
              srcSet={popupImage.desktop.url}
            />
            <img
              src={popupImage.mobile.url}
              width={popupImage.mobile.width}
              height={popupImage.mobile.height}
              loading="eager"
              alt={intl.formatMessage(variantConfig.imageAlt)}
            />
          </picture>
          <CloseButtonContainer>
            <IconButton
              kind="secondary"
              variant="dark"
              onClick={onClose}
              aria-label={intl.formatMessage(thereNoPlaceLikeHomePopupMessages.close)}
            >
              <LineTimesIcon />
            </IconButton>
          </CloseButtonContainer>
        </ImageContainer>

        <Content>
          <Group direction="vertical" gap={12}>
            <Group direction="vertical" gap={8}>
              {variantConfig.label && (
                <PromotionLabel variant="default" icon={<SolidSparklesIcon />}>
                  {intl.formatMessage(variantConfig.label)}
                </PromotionLabel>
              )}
              <Group direction="vertical" gap={4}>
                <Heading variant="heading3">
                  {intl.formatMessage(thereNoPlaceLikeHomePopupMessages.title)}
                </Heading>
                <BodyText variant="large" colour="neutral-two">
                  {intl.formatMessage(variantConfig.description)}
                </BodyText>
              </Group>
            </Group>

            {variantConfig.prompts.length > 0 && (
              <Group direction="horizontal" gap="8 8" wrap="wrap">
                {variantConfig.prompts.map(prompt => (
                  <PromptButton
                    key={prompt.actionId}
                    data-there-no-place-like-home-popup-action-id={prompt.actionId}
                    variant="outline"
                    size="large"
                    onClick={onActionClick}
                  >
                    {intl.formatMessage(prompt.message)}
                  </PromptButton>
                ))}
              </Group>
            )}

            <Actions
              direction="vertical"
              tabletDirection="horizontal"
              gap={8}
              horizontalAlign="stretch"
              tabletHorizontalAlign="end"
              tabletVerticalAlign="center"
            >
              {variant === 'control' && (
                <TextButton
                  kind="tertiary"
                  size="large"
                  onClick={onRemindMeNextTimeClick}
                >
                  {intl.formatMessage(thereNoPlaceLikeHomePopupMessages.remindMeNextTime)}
                </TextButton>
              )}
              <PrimaryAction
                data-there-no-place-like-home-popup-action-id={variantConfig.primaryAction.actionId}
                kind="primary"
                size="large"
                startIcon={<SolidSparklesIcon />}
                onClick={onActionClick}
              >
                {intl.formatMessage(variantConfig.primaryAction.message)}
              </PrimaryAction>
            </Actions>
          </Group>
        </Content>
      </ModalBody>
    </ModalBase>
  )
}

export default ThereNoPlaceLikeHomePopupContent
